// WARDEN-7 part textures: a reverse-jointed security walker. Each part is a
// separate texture so the rig can animate legs, torso, head, racks, shutters
// and core independently. Rig geometry (pivots, joint offsets) is exported so
// the behaviour code, hit areas and art never drift apart.

import type Phaser from 'phaser';
import { COLORS } from '../../config/palette';
import {
  artRng,
  bevelEdges,
  bounds,
  breach,
  canvasTex,
  chamfer,
  cracks,
  css,
  dents,
  disc,
  ellipse,
  emissive,
  emissiveDot,
  glowMask,
  groove,
  hub,
  mix,
  poly,
  recess,
  rivets,
  scorch,
  shade,
  softDot,
  solid,
  sphere,
  stencil,
  stripes,
  vents,
  type G,
  type Pt,
} from './artkit';

/** Damage state of a part: 0 pristine, 1 battle-worn (phase 2), 2 breached (phase 3). */
export type Dmg = 0 | 1 | 2;

export const WARDEN_TEX = {
  foot: 'boss_warden_foot',
  shin: 'boss_warden_shin',
  thigh: 'boss_warden_thigh',
  pelvis: (d: Dmg) => `boss_warden_pelvis_${d}`,
  torso: (d: Dmg) => `boss_warden_torso_${d}`,
  shutterTop: 'boss_warden_shutter_top',
  shutterBot: 'boss_warden_shutter_bot',
  core: 'boss_warden_core',
  coreGlow: 'boss_warden_core_glow',
  head: (d: Dmg) => `boss_warden_head_${d}`,
  headGlow: 'boss_warden_head_glow',
  rack: (d: Dmg) => `boss_warden_rack_${d}`,
  rackLid: 'boss_warden_rack_lid',
  rackGlow: 'boss_warden_rack_glow',
  antenna: 'boss_warden_antenna',
  antennaGlow: 'boss_warden_antenna_glow',
} as const;

/**
 * Rig geometry in pixels, facing right, y down. "Root" is the ground point
 * under the hips. Texture pivots are given in texture pixels.
 */
export const WARDEN_RIG = {
  hipHeight: 160,
  thighLen: 70,
  shinLen: 84,
  ankleHeight: 28,
  /** Hip joints relative to the hip centre. */
  hipFront: { x: 14, y: 12 },
  hipRear: { x: -18, y: 9 },
  /** Foot rest x relative to the root. */
  footFront: 52,
  footRear: -56,
  /** Torso pivot relative to the hip centre. */
  torsoPivot: { x: -4, y: -20 },
  /** Points in torso space (relative to the torso pivot). */
  core: { x: -8, y: -64 },
  neck: { x: 70, y: -104 },
  rackRear: { x: -52, y: -122 },
  rackFront: { x: -12, y: -127 },
  antenna: { x: -88, y: -100 },
  backVent: { x: -94, y: -62 },
  /** Lens relative to the neck pivot (head space). */
  lens: { x: 86, y: -12 },
  /** Rack tube mouths relative to the rack mount. */
  tubes: [
    { x: -10, y: -61 },
    { x: 11, y: -63 },
    { x: 33, y: -65 },
  ],
  /** Rack lid hinge relative to the rack mount. */
  lidHinge: { x: -36, y: -58 },
  /** Beacon relative to the antenna base. */
  beacon: { x: 0, y: -96 },
  shutterTravel: 30,
  // texture sizes + pivots
  tex: {
    foot: { w: 110, h: 46, ox: 46, oy: 14 },
    shin: { w: 50, h: 118, ox: 25, oy: 15 },
    thigh: { w: 58, h: 106, ox: 29, oy: 17 },
    pelvis: { w: 144, h: 80, ox: 72, oy: 40 },
    torso: { w: 228, h: 160, ox: 112, oy: 148 },
    shutterTop: { w: 84, h: 46, ox: 42, oy: 44 },
    shutterBot: { w: 84, h: 46, ox: 42, oy: 2 },
    core: { w: 72, h: 72, ox: 36, oy: 36 },
    coreGlow: { w: 200, h: 200, ox: 100, oy: 100 },
    head: { w: 140, h: 84, ox: 38, oy: 56 },
    rack: { w: 110, h: 90, ox: 46, oy: 82 },
    rackLid: { w: 90, h: 24, ox: 10, oy: 16 },
    antenna: { w: 36, h: 118, ox: 18, oy: 112 },
  },
} as const;

const P = {
  armor: 0x5a6680,
  plate: 0x8592a8,
  frame: 0x2a313d,
  joint: 0x39414f,
  paint: 0xd08a2a,
  stripe: 0x17191d,
  glow: COLORS.threat,
  glowHot: 0xff6a7f,
};

const hot = (d: Dmg): number => (d === 2 ? COLORS.threatHot : COLORS.threat);

// ───────────── legs ─────────────

function drawFoot(g: G): void {
  const shell: Pt[] = [
    [12, 40],
    [6, 30],
    [16, 18],
    [34, 10],
    [62, 10],
    [84, 18],
    [102, 28],
    [106, 38],
    [100, 42],
  ];
  // Toe talons first (behind the shell).
  for (const [x, y] of [
    [88, 36],
    [70, 38],
  ] as Pt[]) {
    const talon = poly([
      [x, y - 4],
      [x + 22, y + 4],
      [x + 4, y + 6],
    ]);
    solid(g, talon, { x, y: y - 4, w: 22, h: 10 }, P.frame, { bevel: 1 });
  }
  const s = poly(shell);
  solid(g, s, bounds(shell), P.armor, { bevel: 2.5 });
  // Sole
  const sole = chamfer(8, 36, 96, 8, [2, 2, 3, 3]);
  solid(g, sole, { x: 8, y: 36, w: 96, h: 8 }, P.frame, { bevel: 1 });
  groove(g, [
    [24, 22],
    [62, 18],
    [92, 30],
  ], P.armor);
  // Hazard toe cap
  const cap = poly([
    [82, 20],
    [102, 30],
    [104, 36],
    [84, 34],
  ]);
  stripes(g, cap, { x: 82, y: 20, w: 22, h: 16 }, P.paint, P.stripe, 4);
  // Ankle hub
  hub(g, 46, 14, 12, P.joint, 4);
  emissiveDot(g, 96, 33, 2.2, COLORS.threat, 4);
  rivets(g, [
    [22, 30],
    [36, 28],
    [64, 26],
  ], P.armor, 1.8);
}

function drawShin(g: G): void {
  // Rear hydraulic piston (behind the strut).
  const piston = chamfer(6, 30, 10, 62, 3);
  solid(g, piston, { x: 6, y: 30, w: 10, h: 62 }, 0x9aa6b8, { bevel: 1.5, hi: 1.5 });
  const strut: Pt[] = [
    [8, 18],
    [42, 18],
    [37, 98],
    [14, 102],
  ];
  solid(g, poly(strut), bounds(strut), P.armor, { bevel: 2.5 });
  // Front armour plate
  const plate: Pt[] = [
    [26, 26],
    [44, 30],
    [40, 76],
    [27, 80],
  ];
  solid(g, poly(plate), bounds(plate), P.plate, { bevel: 2 });
  groove(g, [
    [20, 34],
    [18, 88],
  ], P.armor);
  stripes(g, poly([
    [27, 62],
    [41, 60],
    [40, 74],
    [27, 78],
  ]), { x: 26, y: 60, w: 16, h: 18 }, P.paint, P.stripe, 3.5);
  hub(g, 25, 15, 14, P.joint, 4);
  hub(g, 25, 99, 9, P.joint, 3);
}

function drawThigh(g: G): void {
  const body: Pt[] = [
    [6, 24],
    [52, 20],
    [46, 86],
    [14, 90],
  ];
  solid(g, poly(body), bounds(body), P.armor, { bevel: 2.5 });
  const plate: Pt[] = [
    [10, 34],
    [48, 32],
    [44, 62],
    [14, 66],
  ];
  solid(g, poly(plate), bounds(plate), P.plate, { bevel: 2 });
  groove(g, [
    [16, 48],
    [44, 46],
  ], P.plate);
  rivets(g, [
    [16, 38],
    [42, 37],
    [17, 60],
    [40, 58],
  ], P.plate, 1.8);
  vents(g, 18, 70, 24, 12, 3, P.armor);
  hub(g, 29, 17, 18, P.joint, 6);
  hub(g, 29, 87, 12, P.joint, 4);
}

function drawPelvis(g: G, d: Dmg): void {
  const rng = artRng(`warden-pelvis-${d}`);
  const block = chamfer(10, 14, 124, 52, [10, 14, 10, 14]);
  solid(g, block, { x: 10, y: 14, w: 124, h: 52 }, P.frame, { bevel: 2.5, hi: 1.6 });
  const top = chamfer(26, 10, 92, 20, [6, 6, 2, 2]);
  solid(g, top, { x: 26, y: 10, w: 92, h: 20 }, P.armor, { bevel: 2 });
  stripes(g, chamfer(18, 52, 108, 12, [2, 2, 8, 8]), { x: 18, y: 52, w: 108, h: 12 }, P.paint, P.stripe, 6);
  // Hip sockets
  hub(g, 86, 52, 14, P.joint, 4);
  hub(g, 54, 49, 12, shade(P.joint, 0.8), 4);
  // Central gimbal
  hub(g, 72, 32, 15, P.armor, 6);
  groove(g, [
    [16, 40],
    [40, 40],
  ], P.frame);
  groove(g, [
    [104, 40],
    [128, 40],
  ], P.frame);
  if (d >= 1) {
    dents(g, rng, { x: 18, y: 18, w: 108, h: 40 }, 6, P.frame);
    scorch(g, 110, 30, 18, 0.5);
  }
  if (d >= 2) {
    const hole = poly([
      [20, 24],
      [42, 20],
      [46, 44],
      [24, 50],
    ]);
    breach(g, rng, hole, { x: 20, y: 20, w: 26, h: 30 }, P.frame, COLORS.threatHot);
    cracks(g, rng, 100, 22, 22, { branches: 2, base: P.frame });
  }
}

// ───────────── torso ─────────────

const TORSO_SHAPE: Pt[] = [
  [28, 140],
  [20, 96],
  [28, 54],
  [54, 30],
  [122, 22],
  [170, 28],
  [202, 54],
  [214, 92],
  [202, 130],
  [180, 148],
  [46, 150],
];

function drawTorso(g: G, d: Dmg): void {
  const rng = artRng(`warden-torso-${d}`);
  const shell = poly(TORSO_SHAPE);
  solid(g, shell, bounds(TORSO_SHAPE), P.armor, { bevel: 3, hi: 1.45 });

  // Dark belly frame.
  const belly: Pt[] = [
    [40, 128],
    [194, 124],
    [180, 148],
    [46, 150],
  ];
  solid(g, poly(belly), bounds(belly), P.frame, { bevel: 1.5, outline: false });
  vents(g, 70, 132, 90, 10, 6, P.frame, true);

  // Back vent block.
  const back = chamfer(26, 66, 26, 56, 4);
  solid(g, back, { x: 26, y: 66, w: 26, h: 56 }, P.frame, { bevel: 1.5 });
  vents(g, 30, 70, 18, 48, 5, P.frame);

  // Pale shoulder armour plate (the security livery).
  const shoulder: Pt[] = [
    [54, 34],
    [122, 26],
    [168, 32],
    [194, 54],
    [154, 62],
    [62, 60],
  ];
  solid(g, poly(shoulder), bounds(shoulder), P.plate, { bevel: 2.5, hi: 1.3 });
  groove(g, [
    [66, 50],
    [150, 52],
  ], P.plate);
  stencil(g, 'W-7', 96, 44, 15, P.stripe, 0.85, -0.04);
  rivets(g, [
    [64, 40],
    [118, 33],
    [160, 38],
    [184, 54],
  ], P.plate, 2);

  // Front chest plate with hazard stripes.
  const chest: Pt[] = [
    [176, 66],
    [208, 88],
    [198, 124],
    [168, 118],
  ];
  const chestPath = poly(chest);
  solid(g, chestPath, bounds(chest), P.paint, { bevel: 2 });
  stripes(g, chestPath, bounds(chest), P.paint, P.stripe, 7);
  bevelEdges(g, chestPath, P.paint, 2);

  // Core housing ring (core sprite sits in the centre at (104, 84)).
  const ring = disc(104, 84, 38);
  solid(g, ring, { x: 66, y: 46, w: 76, h: 76 }, P.joint, { bevel: 3 });
  recess(g, disc(104, 84, 29), 0x0a0c10, 4);
  const bolts: Pt[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    bolts.push([104 + Math.cos(a) * 33.5, 84 + Math.sin(a) * 33.5]);
  }
  rivets(g, bolts, P.joint, 2);

  groove(g, [
    [30, 60],
    [56, 64],
    [62, 120],
  ], P.armor);
  groove(g, [
    [150, 66],
    [164, 114],
  ], P.armor);
  emissiveDot(g, 194, 64, 3, COLORS.threat, 6);

  if (d >= 1) {
    cracks(g, rng, 140, 40, 30, { branches: 3, base: P.plate });
    scorch(g, 182, 96, 22, 0.55);
    dents(g, rng, { x: 60, y: 30, w: 130, h: 90 }, 12, P.armor);
  }
  if (d >= 2) {
    const hole = poly([
      [36, 40],
      [62, 34],
      [70, 62],
      [56, 76],
      [32, 70],
    ]);
    breach(g, rng, hole, { x: 32, y: 34, w: 38, h: 42 }, P.armor, COLORS.threatHot);
    cracks(g, rng, 96, 118, 26, { branches: 2, base: P.armor });
    cracks(g, rng, 176, 40, 20, { branches: 2, glow: COLORS.threatHot, width: 1.6 });
    scorch(g, 60, 104, 26, 0.5);
  }
}

function drawShutter(g: G, top: boolean): void {
  const cx = 42;
  const cy = top ? 44 : 2;
  const p = new Path2D();
  if (top) {
    p.moveTo(cx - 36, cy);
    p.arc(cx, cy, 36, Math.PI, Math.PI * 2);
    p.closePath();
  } else {
    p.moveTo(cx + 36, cy);
    p.arc(cx, cy, 36, 0, Math.PI);
    p.closePath();
  }
  solid(g, p, { x: cx - 36, y: top ? cy - 36 : cy, w: 72, h: 36 }, P.plate, { bevel: 2.5, hi: 1.3 });
  // Warning chevron and bolts.
  const chev = top
    ? poly([
        [cx - 12, cy - 10],
        [cx, cy - 20],
        [cx + 12, cy - 10],
        [cx + 12, cy - 5],
        [cx, cy - 14],
        [cx - 12, cy - 5],
      ])
    : poly([
        [cx - 12, cy + 10],
        [cx, cy + 20],
        [cx + 12, cy + 10],
        [cx + 12, cy + 5],
        [cx, cy + 14],
        [cx - 12, cy + 5],
      ]);
  emissive(g, chev, { x: cx - 12, y: top ? cy - 20 : cy + 5, w: 24, h: 15 }, COLORS.threat, { blur: 4, heat: 0.4 });
  rivets(g, top
    ? [
        [cx - 26, cy - 8],
        [cx + 26, cy - 8],
      ]
    : [
        [cx - 26, cy + 8],
        [cx + 26, cy + 8],
      ], P.plate, 2);
  groove(g, top
    ? [
        [cx - 34, cy - 3],
        [cx + 34, cy - 3],
      ]
    : [
        [cx - 34, cy + 3],
        [cx + 34, cy + 3],
      ], P.plate, 1.2);
}

function drawCore(g: G): void {
  softDot(g, 36, 36, 34, COLORS.threat, 0.5);
  sphere(g, 36, 36, 25, 0xfff1f4, 0xb3122e, { spec: 0.7 });
  // Inner reactor iris
  g.strokeStyle = css(0x5a0a18, 0.8);
  g.lineWidth = 2;
  g.beginPath();
  g.arc(36, 36, 14, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = 'rgba(255,255,255,0.6)';
  g.lineWidth = 1.5;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    g.beginPath();
    g.moveTo(36 + Math.cos(a) * 6, 36 + Math.sin(a) * 6);
    g.lineTo(36 + Math.cos(a) * 20, 36 + Math.sin(a) * 20);
    g.stroke();
  }
}

// ───────────── head ─────────────

const HEAD_SHAPE: Pt[] = [
  [12, 68],
  [10, 34],
  [24, 20],
  [58, 12],
  [102, 18],
  [126, 30],
  [134, 46],
  [122, 60],
  [90, 68],
  [56, 72],
];

function headVisor(): Path2D {
  return poly([
    [64, 32],
    [126, 38],
    [124, 50],
    [66, 46],
  ]);
}

function drawHead(g: G, d: Dmg): void {
  const rng = artRng(`warden-head-${d}`);
  // Antenna stubs behind the head.
  g.strokeStyle = css(COLORS.ink);
  g.lineWidth = 4;
  g.beginPath();
  g.moveTo(24, 24);
  g.lineTo(18, 4);
  g.moveTo(34, 18);
  g.lineTo(32, 2);
  g.stroke();
  g.strokeStyle = css(P.joint);
  g.lineWidth = 2;
  g.stroke();

  const shell = poly(HEAD_SHAPE);
  solid(g, shell, bounds(HEAD_SHAPE), P.armor, { bevel: 3, hi: 1.45 });
  // Cheek plate.
  const cheek: Pt[] = [
    [16, 40],
    [56, 38],
    [62, 66],
    [18, 66],
  ];
  solid(g, poly(cheek), bounds(cheek), P.plate, { bevel: 2 });
  stripes(g, poly([
    [20, 56],
    [60, 54],
    [62, 64],
    [20, 64],
  ]), { x: 20, y: 54, w: 42, h: 10 }, P.paint, P.stripe, 4);
  // Light bar on top (police-style beacon strip).
  const bar = chamfer(36, 6, 42, 9, 3);
  solid(g, bar, { x: 36, y: 6, w: 42, h: 9 }, P.frame, { bevel: 1 });
  emissive(g, chamfer(39, 8, 36, 4, 1.5), { x: 39, y: 8, w: 36, h: 4 }, hot(d), { blur: 5 });
  // Visor slit.
  recess(g, headVisor(), 0x07080b, 3);
  emissive(g, poly([
    [68, 38],
    [122, 42],
    [122, 45],
    [68, 42],
  ]), { x: 68, y: 38, w: 54, h: 7 }, hot(d), { blur: 6 });
  // Emitter lens housing + lens.
  hub(g, 124, 45, 10, P.frame, 0);
  emissiveDot(g, 124, 45, 6, hot(d), 10);
  groove(g, [
    [62, 24],
    [104, 26],
  ], P.armor);
  rivets(g, [
    [24, 30],
    [50, 22],
    [96, 24],
  ], P.armor, 1.8);
  hub(g, 38, 56, 11, P.joint, 4);
  if (d >= 1) {
    dents(g, rng, { x: 20, y: 18, w: 90, h: 44 }, 6, P.armor);
  }
  if (d >= 2) {
    // Cracked visor leaking light.
    cracks(g, rng, 96, 42, 18, { branches: 3, glow: COLORS.threatHot, width: 1.4 });
    scorch(g, 84, 60, 16, 0.6);
  }
}

function drawHeadGlow(g: G): void {
  glowMask(g, poly([
    [68, 38],
    [122, 42],
    [122, 45],
    [68, 42],
  ]), 10);
  glowMask(g, disc(124, 45, 7), 14);
  glowMask(g, chamfer(39, 8, 36, 4, 1.5), 8, 0.9);
}

// ───────────── shoulder mortar rack ─────────────

const RACK_SHAPE: Pt[] = [
  [12, 74],
  [10, 34],
  [20, 22],
  [92, 16],
  [102, 28],
  [100, 74],
];

function drawRack(g: G, d: Dmg): void {
  const rng = artRng(`warden-rack-${d}`);
  const mount = chamfer(34, 70, 26, 18, 3);
  solid(g, mount, { x: 34, y: 70, w: 26, h: 18 }, P.frame, { bevel: 1.5 });
  const box = poly(RACK_SHAPE);
  solid(g, box, bounds(RACK_SHAPE), P.armor, { bevel: 3 });
  // Tube mouths on the top face.
  for (const [x, y] of [
    [36, 22],
    [57, 20],
    [79, 18],
  ] as Pt[]) {
    const m = ellipse(x, y, 9, 4.5, -0.06);
    g.fillStyle = css(COLORS.ink);
    g.fill(ellipse(x, y, 11, 6, -0.06));
    recess(g, m, 0x050608, 2);
    softDot(g, x, y + 1, 7, COLORS.threat, d === 2 && x > 70 ? 0.15 : 0.55);
  }
  stripes(g, chamfer(14, 44, 86, 14, 2), { x: 14, y: 44, w: 86, h: 14 }, P.paint, P.stripe, 7);
  groove(g, [
    [16, 36],
    [98, 34],
  ], P.armor);
  vents(g, 22, 60, 70, 10, 5, P.armor, true);
  rivets(g, [
    [18, 30],
    [96, 26],
    [18, 66],
    [96, 66],
  ], P.armor, 1.8);
  if (d >= 1) dents(g, rng, { x: 16, y: 28, w: 80, h: 40 }, 5, P.armor);
  if (d >= 2) {
    scorch(g, 76, 30, 20, 0.7);
    cracks(g, rng, 70, 40, 22, { branches: 2, base: P.armor });
    // Ruptured tube.
    g.strokeStyle = css(COLORS.ink);
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(72, 14);
    g.lineTo(78, 8);
    g.lineTo(84, 14);
    g.stroke();
  }
}

function drawRackLid(g: G): void {
  const lid = chamfer(6, 7, 80, 11, [2, 6, 2, 2]);
  solid(g, lid, { x: 6, y: 7, w: 80, h: 11 }, P.plate, { bevel: 2 });
  stripes(g, chamfer(56, 9, 26, 7, 1), { x: 56, y: 9, w: 26, h: 7 }, P.paint, P.stripe, 3);
  hub(g, 10, 16, 5, P.joint, 0);
}

function drawRackGlow(g: G): void {
  for (const [x, y] of [
    [36, 22],
    [57, 20],
    [79, 18],
  ] as Pt[]) {
    glowMask(g, ellipse(x, y, 8, 4, -0.06), 10);
  }
}

// ───────────── antenna ─────────────

function drawAntenna(g: G): void {
  const mast = chamfer(15, 20, 6, 88, 1);
  solid(g, mast, { x: 15, y: 20, w: 6, h: 88 }, P.joint, { bevel: 1 });
  for (let y = 34; y < 100; y += 16) {
    const ringP = chamfer(12, y, 12, 4, 1);
    solid(g, ringP, { x: 12, y, w: 12, h: 4 }, P.frame, { bevel: 0.5 });
  }
  const base = chamfer(8, 100, 20, 14, 3);
  solid(g, base, { x: 8, y: 100, w: 20, h: 14 }, P.frame, { bevel: 1.5 });
  const dome = new Path2D();
  dome.moveTo(8, 20);
  dome.arc(18, 20, 10, Math.PI, Math.PI * 2);
  dome.closePath();
  solid(g, dome, { x: 8, y: 10, w: 20, h: 10 }, P.frame, { bevel: 1 });
  emissiveDot(g, 18, 15, 5, COLORS.threat, 8);
}

function drawAntennaGlow(g: G): void {
  glowMask(g, disc(18, 15, 6), 12);
}

/** All WARDEN-7 textures (pristine + damaged variants). */
export function buildWardenArt(scene: Phaser.Scene): void {
  const T = WARDEN_RIG.tex;
  canvasTex(scene, WARDEN_TEX.foot, T.foot.w, T.foot.h, drawFoot);
  canvasTex(scene, WARDEN_TEX.shin, T.shin.w, T.shin.h, drawShin);
  canvasTex(scene, WARDEN_TEX.thigh, T.thigh.w, T.thigh.h, drawThigh);
  for (const d of [0, 1, 2] as Dmg[]) {
    canvasTex(scene, WARDEN_TEX.pelvis(d), T.pelvis.w, T.pelvis.h, (g) => drawPelvis(g, d));
    canvasTex(scene, WARDEN_TEX.torso(d), T.torso.w, T.torso.h, (g) => drawTorso(g, d));
    canvasTex(scene, WARDEN_TEX.head(d), T.head.w, T.head.h, (g) => drawHead(g, d));
    canvasTex(scene, WARDEN_TEX.rack(d), T.rack.w, T.rack.h, (g) => drawRack(g, d));
  }
  canvasTex(scene, WARDEN_TEX.shutterTop, T.shutterTop.w, T.shutterTop.h, (g) => drawShutter(g, true));
  canvasTex(scene, WARDEN_TEX.shutterBot, T.shutterBot.w, T.shutterBot.h, (g) => drawShutter(g, false));
  canvasTex(scene, WARDEN_TEX.core, T.core.w, T.core.h, drawCore);
  canvasTex(scene, WARDEN_TEX.coreGlow, T.coreGlow.w, T.coreGlow.h, (g) => {
    softDot(g, 100, 100, 100, 0xffffff, 0.9);
    softDot(g, 100, 100, 40, 0xffffff, 1);
  });
  canvasTex(scene, WARDEN_TEX.headGlow, T.head.w, T.head.h, drawHeadGlow);
  canvasTex(scene, WARDEN_TEX.rackLid, T.rackLid.w, T.rackLid.h, drawRackLid);
  canvasTex(scene, WARDEN_TEX.rackGlow, T.rack.w, T.rack.h, drawRackGlow);
  canvasTex(scene, WARDEN_TEX.antenna, T.antenna.w, T.antenna.h, drawAntenna);
  canvasTex(scene, WARDEN_TEX.antennaGlow, T.antenna.w, T.antenna.h, drawAntennaGlow);
}

export { mix };
